import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/config/app";
import type { PaginatedResponse, PaginationParams } from "@/types/api";
import { withAdminCurrency } from "@/utils/adminCurrency";
import { buildUrl } from "@/utils/buildUrl";

import type {
  FundingBrandStat,
  FundingConsumptionResponse,
  IApplicationInfo,
  IFundingApplications,
  IPayout,
  IUpdateFundingCreditPeriodRequest,
  IUpdateFundingTermsRequest,
  IUpdateTransferRequest,
  PayoutGroup,
} from "./types";

export const fundingApi = {
  async getAllFundings(
    groupId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<IPayout>> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/fundings/group/${groupId}`,
      withAdminCurrency(params)
    );
    return await apiClient.get<PaginatedResponse<IPayout>>(url);
  },

  async postFunding(brandData: PayoutGroup) {
    return await apiClient.post<Omit<PayoutGroup, "id">>(
      `${API_ENDPOINTS.ADMIN}/fundings`,
      brandData
    );
  },

  async getFundingApplication(
    brandId: string,
    params?: PaginationParams
  ): Promise<PaginatedResponse<IFundingApplications>> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/fundings/brand/${brandId}`,
      withAdminCurrency(params)
    );
    return await apiClient.get<PaginatedResponse<IFundingApplications>>(url);
  },

  async getFundingConsumption(
    brandId: string,
    params?: PaginationParams
  ): Promise<FundingConsumptionResponse> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/fundings/brand/${brandId}/consumption`,
      withAdminCurrency(params)
    );
    return await apiClient.get<FundingConsumptionResponse>(url);
  },

  async getApplicationById(id: string): Promise<IApplicationInfo> {
    return await apiClient.get<IApplicationInfo>(
      `${API_ENDPOINTS.ADMIN}/fundings/${id}`
    );
  },

  async updateFundingTerms(id: string, data: IUpdateFundingTermsRequest) {
    return await apiClient.patch<IApplicationInfo>(
      `${API_ENDPOINTS.ADMIN}/fundings/${id}`,
      data
    );
  },

  async approveFunding(id: string) {
    return await apiClient.post(
      `${API_ENDPOINTS.ADMIN}/fundings/${id}/approve`
    );
  },

  async updateFundingCreditPeriod(
    id: string,
    data: IUpdateFundingCreditPeriodRequest
  ) {
    return await apiClient.patch<IApplicationInfo>(
      `${API_ENDPOINTS.ADMIN}/fundings/${id}/credit-period`,
      data
    );
  },

  async addTransferDetails(id: string, data: IUpdateTransferRequest) {
    return await apiClient.patch(
      `${API_ENDPOINTS.ADMIN}/fundings/${id}/transfer`,
      data
    );
  },

  async rejectFunding(id: string) {
    return await apiClient.post(`${API_ENDPOINTS.ADMIN}/fundings/${id}/reject`);
  },

  async getFundingGroupConsumption(
    groupId: string,
    params?: PaginationParams
  ): Promise<FundingConsumptionResponse> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/fundings/group/${groupId}/consumption`,
      withAdminCurrency(params)
    );
    return await apiClient.get<FundingConsumptionResponse>(url);
  },

  async getFundingStatisticById(
    id: string,
    params?: { startDate: string; endDate: string }
  ): Promise<FundingBrandStat> {
    const url = buildUrl(
      API_ENDPOINTS.ADMIN,
      `/fundings/brand/${id}/stats`,
      params
    );
    return await apiClient.get<FundingBrandStat>(url);
  },
};
